import { createEnum } from '../helpers'

/**
 * The financial metrics that Google Financial tracks per ERP
 */
export const GOOGLE_FINANCIAL_STATEMENT_TYPES = [
  'netProfit',
  'grossMargin',
  'currentRatio',
  'quickRatio',
  'debtToAssetRatio',
  'averageCollectionPeriod',
  'averageDaysPayable',
] as const

export type GoogleFinancialStatementType = (typeof GOOGLE_FINANCIAL_STATEMENT_TYPES)[number]

/**
 * How a value is rendered on the frontend. Typed here so the backend stays the single source of
 * truth and the UI never hard-codes which metrics are percentages vs day counts.
 */
export type GoogleFinancialStatementValueFormat = 'percentage' | 'days'

export const GOOGLE_FINANCIAL_STATEMENT_TYPE_FORMAT: Record<
  GoogleFinancialStatementType,
  GoogleFinancialStatementValueFormat
> = {
  netProfit: 'percentage',
  grossMargin: 'percentage',
  currentRatio: 'percentage',
  quickRatio: 'percentage',
  debtToAssetRatio: 'percentage',
  averageCollectionPeriod: 'days',
  averageDaysPayable: 'days',
}

/**
 * CSV header label for each type. Shared by the import schema and the export so the file
 * round-trips: users export, edit values, and re-import the same shape.
 */
export const GOOGLE_FINANCIAL_STATEMENT_TYPE_HEADERS: Record<GoogleFinancialStatementType, string> =
  {
    netProfit: 'Net Profit %',
    grossMargin: 'GM %',
    currentRatio: 'Current Ratio %',
    quickRatio: 'Quick Ratio %',
    debtToAssetRatio: 'Debt-to-Asset Ratio %',
    averageCollectionPeriod: 'Average Collection Period (Days)',
    averageDaysPayable: 'Average Days Payable (Days)',
  }

/**
 * CSV header label for each type's comment column, paired with the value column above. Derived
 * from the value header so the two can never drift apart.
 */
export const GOOGLE_FINANCIAL_STATEMENT_TYPE_COMMENT_HEADERS: Record<
  GoogleFinancialStatementType,
  string
> = Object.fromEntries(
  GOOGLE_FINANCIAL_STATEMENT_TYPES.map((type) => [
    type,
    `${GOOGLE_FINANCIAL_STATEMENT_TYPE_HEADERS[type]} Comment`,
  ]),
) as Record<GoogleFinancialStatementType, string>

/** Longest note the import and the in-app editor accept for a single cell. */
export const GOOGLE_FINANCIAL_STATEMENT_COMMENT_MAX_LENGTH = 5000

/**
 * The trend window: how many years of data to show in the trend chart (inclusive of the current year).
 */
export const GOOGLE_FINANCIAL_STATEMENTS_DEFAULT_YEAR_SPAN = 6

export const GOOGLE_FINANCIAL_STATEMENTS_IMPORT_ERRORS = [
  'zodSchemaError',
  'missingErp',
  'invalidYear',
  'invalidValue',
] as const

export const GOOGLE_FINANCIAL_STATEMENTS_IMPORT_ERROR_TYPES = createEnum(
  GOOGLE_FINANCIAL_STATEMENTS_IMPORT_ERRORS,
)
export type GoogleFinancialStatementsImportErrorType =
  (typeof GOOGLE_FINANCIAL_STATEMENTS_IMPORT_ERRORS)[number]
